/**
 * History › Calls: every call that touched this project, with each action it took labelled
 * "via call" (sealed `call` records written by the Mac; actions attributed from the signed
 * commands' callId, never from the agent). Read on open, no polling.
 */

import { useQuery } from '@tanstack/react-query'
import { CallRecord } from '@viko/protocol'
import { KeyRound, Phone } from 'lucide-react'
import { useBackend } from '../../lib/backend.js'
import { formatCallTime } from './CallTimer.js'
import './call.css'

export function callsForProject(rows: { id: string; value: unknown }[], projectId: string) {
  return rows
    .map((r) => ({ id: r.id, rec: CallRecord.safeParse(r.value) }))
    .filter((x) => x.rec.success)
    .map((x) => ({ id: x.id, rec: (x.rec as { data: CallRecord }).data }))
    .filter(
      (x) =>
        x.rec.projectIds.includes(projectId) ||
        x.rec.actions.some((a) => a.projectId === projectId),
    )
    .sort((a, b) => b.rec.startedAt - a.rec.startedAt)
}

export function CallHistory({ projectId }: { projectId: string }) {
  const b = useBackend()
  const entities = b.entities
  const q = useQuery({
    queryKey: ['calls', b.artifactAccess.accountId, projectId],
    queryFn: async () => (entities ? callsForProject(await entities.list('call'), projectId) : []),
    enabled: !!entities,
    retry: false,
  })
  const calls = q.data ?? []
  if (!calls.length) return <p className="call-meta">No calls have touched this project yet.</p>
  return (
    <div className="call-batch__list" data-testid="call-history">
      {calls.map(({ id, rec }) => {
        const actions = rec.actions.filter((a) => !a.projectId || a.projectId === projectId)
        return (
          <div key={id} className="call-batch__row">
            <Phone size={18} aria-hidden="true" />
            <span>
              <span className="call-batch__t">
                Call · {new Date(rec.startedAt).toLocaleString()}
                {rec.endedAt ? ` · ${formatCallTime(rec.endedAt - rec.startedAt)}` : ''}
              </span>
              {actions.map((a) => (
                <span key={a.id} className="call-meta" style={{ display: 'block' }}>
                  {a.tier === 'secure' ? <KeyRound size={14} aria-hidden="true" /> : null} {a.title}
                  {a.state !== 'done' ? ` (${a.state})` : ''} · via call
                </span>
              ))}
            </span>
            <span className="call-meta">{rec.mode === 'local' ? 'Local' : 'Live voice'}</span>
          </div>
        )
      })}
    </div>
  )
}
